import React, { useEffect } from "react";
import { clearCustomAuthTokens } from "../features/auth/LoginBox";
import type { SessionExpiredReason } from "../utils/sessionEvents";

export type SessionExpiredModalProps = {
  reason?: SessionExpiredReason;
};

const COPY: Record<
  SessionExpiredReason,
  { title: string; body: string; cta: string }
> = {
  expired: {
    title: "Session Expired",
    body: "Your session has expired. Please sign in again to continue.",
    cta: "Sign In",
  },
  logout: {
    title: "You've been logged out",
    body: "You have been successfully logged out.",
    cta: "Sign In",
  },
};

export const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({
  reason = "expired",
}) => {
  // Block ESC key while modal is visible
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  const handleSignIn = (): void => {
    // Tokens are already cleared before this modal is shown.
    // Belt-and-suspenders: ensure nothing remains before navigating to login.
    clearCustomAuthTokens();
    localStorage.clear();
    sessionStorage.clear();
    // Navigate to "/" — AppRouter will show <LoginBox /> since no tokens exist.
    window.location.replace("/");
  };

  const copy = COPY[reason];

  return (
    // Backdrop — non-dismissible, no onClick handler
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-modal-title"
      aria-describedby="session-modal-desc"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        pointerEvents: "all",
      }}
    >
      {/* Modal card */}
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        className="bg-card text-card-foreground"
        style={{
          borderRadius: "12px",
          padding: "40px 32px",
          maxWidth: "420px",
          width: "calc(100% - 48px)",
          textAlign: "center",
          boxShadow: "0 24px 64px rgba(0, 0, 0, 0.35)",
        }}
      >
        {/* Lock icon */}
        <div aria-hidden="true" style={{ fontSize: "40px", marginBottom: "20px" }}>
          🔒
        </div>

        {/* Title */}
        <h2
          id="session-modal-title"
          className="text-foreground"
          style={{
            margin: "0 0 12px",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          {copy.title}
        </h2>

        {/* Body */}
        <p
          id="session-modal-desc"
          className="text-muted-foreground"
          style={{
            margin: "0 0 28px",
            fontSize: "14px",
            lineHeight: 1.6,
          }}
        >
          {copy.body}
        </p>

        {/* CTA — redirects to login only on explicit click */}
        <button
          onClick={handleSignIn}
          className="bg-primary text-primary-foreground px-6 py-2 rounded-lg font-medium cursor-pointer hover:opacity-90 transition-opacity"
        >
          {copy.cta}
        </button>
      </div>
    </div>
  );
};
